import { cn } from '@/lib/utils';
import { VariantImage } from '@/components/variant-image';
import { useI18n } from '@/providers/i18n';
import { useMemo } from 'react';
import { imageModels } from '@flowmuse/models/image';
import { Image as ImageIcon } from 'lucide-react';
import { pixelSizeToCommonRatio } from '@flowmuse/models/shared/image-size';
import { resolveAssetRefPreview } from '@flowmuse/models/shared/asset-ref';

type ImageCollapsedSummaryProps = {
  data: Record<string, any> | undefined;
  className?: string;
};

const inferNameFromUrl = (url: string): string | undefined => {
  try {
    const u = new URL(String(url), typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const parts = u.pathname.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    if (!last) return undefined;
    return decodeURIComponent(last);
  } catch {
    try {
      const s = String(url);
      const last = s.split('?')[0]?.split('#')[0]?.split('/').filter(Boolean).pop();
      return last ? decodeURIComponent(last) : undefined;
    } catch {
      return undefined;
    }
  }
};

const inferSuffixFromName = (name: string): string | undefined => {
  try {
    const base = String(name).trim();
    if (!base) return undefined;
    const lastDot = base.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === base.length - 1) return undefined;
    const ext = base.slice(lastDot + 1).trim().toLowerCase();
    return ext || undefined;
  } catch {
    return undefined;
  }
};

const inferExtFromMime = (mime: string): string | undefined => {
  try {
    const m = String(mime).toLowerCase().trim();
    if (!m) return undefined;
    if (m === 'image/jpeg' || m === 'image/jpg') return 'jpg';
    if (m === 'image/png') return 'png';
    if (m === 'image/webp') return 'webp';
    if (m === 'image/gif') return 'gif';
    const slash = m.indexOf('/');
    if (slash > 0 && slash < m.length - 1) {
      const subtype = m.slice(slash + 1);
      const cleaned = subtype.replace(/^x-/, '').replace(/^vnd\./, '').replace(/^x\./, '');
      if (/^[a-z0-9.+-]{1,16}$/.test(cleaned)) return cleaned;
    }
  } catch {}
  return undefined;
};

const SUMMARY_THUMB_TRANSFORM = { width: 128, height: 128, resize: 'cover', quality: 80 } as const;

export const ImageCollapsedSummary = ({ data, className }: ImageCollapsedSummaryProps) => {
  const { t } = useI18n();
  const d = (data ?? {}) as any;
  const autoText = t('Auto');

  const modelId = typeof d.model === 'string' && d.model ? d.model : undefined;
  const modelLabel = (() => {
    if (!modelId) return undefined;
    try {
      const direct = (imageModels as any)?.[modelId]?.label;
      if (typeof direct === 'string' && direct.trim()) return direct.trim();
      if (modelId.includes('/')) {
        const tail = modelId.split('/').pop();
        if (tail) {
          const alt = (imageModels as any)?.[tail]?.label;
          if (typeof alt === 'string' && alt.trim()) return alt.trim();
        }
      }
    } catch {}
    return modelId;
  })();

  // 图像节点的“比例”优先来自生成结果的真实尺寸（generated.size），否则回退为用户选择的 size
  // 展示时：若为像素尺寸 "WxH"，则近似为常用比例（16:9/4:3/…），不在范围内再显示原始 "WxH"
  const aspectRatioRaw =
    (typeof d?.generated?.size === 'string' && d.generated.size ? d.generated.size : undefined) ??
    (typeof d.size === 'string' && d.size ? d.size : undefined);
  const aspectRatio = useMemo(() => {
    if (!aspectRatioRaw) return undefined;
    const s = String(aspectRatioRaw).trim();
    if (!s) return undefined;
    if (s === 'match' || s === 'Auto') return s;
    if (/^\d+x\d+$/i.test(s)) {
      const common = pixelSizeToCommonRatio(s, { tolerance: 0.05 });
      return common ?? s;
    }
    return s;
  }, [aspectRatioRaw]);
  const resolution = typeof d.image_resolution === 'string' && d.image_resolution ? d.image_resolution : undefined;

  const generatedUrl = (() => {
    const g = d.generated;
    if (g && typeof g === 'object') {
      if (typeof g.url === 'string' && g.url) return g.url;
      if (Array.isArray(g.urls) && typeof g.urls[0] === 'string' && g.urls[0]) return g.urls[0];
    }
    return undefined;
  })();

  const contentUrl = typeof d?.content?.url === 'string' ? String(d.content.url) : undefined;
  const generatedMime = typeof d?.generated?.type === 'string' ? String(d.generated.type) : undefined;
  const contentMime = typeof d?.content?.type === 'string' ? String(d.content.type) : undefined;

  const displayType = (() => {
    const base = generatedUrl || contentUrl;
    const byName = base ? inferSuffixFromName(inferNameFromUrl(base) || '') : undefined;
    if (byName) return byName;
    const byMime = generatedMime ? inferExtFromMime(generatedMime) : undefined;
    if (byMime) return byMime;
    const byMime2 = contentMime ? inferExtFromMime(contentMime) : undefined;
    if (byMime2) return byMime2;
    return undefined;
  })();

  const assetPreviewUrl = typeof d?.assetPreviewUrl === 'string' ? String(d.assetPreviewUrl) : undefined;

  // generated.url / content.url 均可能是提交审核后的 Asset:// 引用，统一解析回预览 URL
  const thumbBase = useMemo(
    () =>
      resolveAssetRefPreview(generatedUrl, assetPreviewUrl) ||
      resolveAssetRefPreview(contentUrl, assetPreviewUrl),
    [generatedUrl, contentUrl, assetPreviewUrl],
  );

  return (
    <div className={cn('flex h-full w-full items-start gap-4 p-3', className)}>
      <div className="h-32 w-32 shrink-0 overflow-hidden rounded-2xl border border-sidebar-border/30 bg-sidebar-accent/30 flex items-center justify-center shadow-inner">
        {/* 缩略图缺失先回退原图；原图也失败才显示占位图标 */}
        <VariantImage
          url={thumbBase ?? ''}
          transform={SUMMARY_THUMB_TRANSFORM}
          alt={t('Image thumbnail')}
          width={128}
          height={128}
          className="h-full w-full object-cover transition-transform duration-500 hover:scale-110"
          fallback={<ImageIcon size={28} className="text-muted-foreground/40" />}
        />
      </div>

      <div className="min-w-0 flex-1 flex flex-col h-32">
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/30 mb-1">{t('Image')}</div>
        <div className="text-sm font-bold truncate text-foreground/90">{modelLabel ?? '-'}</div>

        <div className="mt-2 flex flex-1 flex-col text-[11px] leading-snug">
          {/* 中间参数：固定高度容器（3 行占位，保证和视频节点一致） */}
          <div className="grid grid-rows-3 gap-1">
            <div className="truncate">
              <span className="text-muted-foreground/60">{t('Aspect ratio')}:</span>{' '}
              <span className="text-foreground/80 font-medium">{aspectRatio ?? autoText}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground/60">{t('Resolution')}:</span>
              <span className="text-foreground/80 font-mono tabular-nums text-[10px]">{resolution ?? autoText}</span>
            </div>
            <div className="truncate">
              <span className="text-muted-foreground/60">{t('Type')}:</span>{' '}
              <span className="text-foreground/80 font-medium">{displayType ?? '-'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


